import React, { useEffect, useState } from 'react';
import './PrintOrderDetails.scss';
import { getLogo } from '../../../../../functions/helpers/getLogo';
import { METALPRESS } from '../../../../../constants/factories';
import { generalGetRequest } from '../functions/api';

const PrintOrderDetails = ({ order, order_processes, user_details }) => {
    const sortedOrderProcesses = Array.isArray(order_processes)
        ? [...order_processes].sort((a, b) => {
            const orderA = a?.order;
            const orderB = b?.order;
            const numericOrderA = Number(orderA);
            const numericOrderB = Number(orderB);
            const hasOrderA = orderA !== null && orderA !== undefined && String(orderA).trim() !== '' && Number.isFinite(numericOrderA);
            const hasOrderB = orderB !== null && orderB !== undefined && String(orderB).trim() !== '' && Number.isFinite(numericOrderB);

            if (!hasOrderA) return hasOrderB ? 1 : 0;
            if (!hasOrderB) return -1;
            return numericOrderA - numericOrderB;
        })
        : [];
    const [customInputDefinitions, setCustomInputDefinitions] = useState([]);
    const orderType = order?.type?.type ?? order?.type;

    useEffect(() => {
        let isCurrentRequest = true;
        const type = user_details?.factory_id === METALPRESS ? 0 : orderType;

        setCustomInputDefinitions([]);

        if (type === null || type === undefined) {
            return () => {
                isCurrentRequest = false;
            };
        }

        const loadCustomInputDefinitions = async () => {
            const response = await generalGetRequest(
                `/system/order-input/get?type=${type}`
            );
            if (isCurrentRequest && response?.ok && Array.isArray(response.result)) {
                setCustomInputDefinitions(response.result);
            }
        };

        loadCustomInputDefinitions();

        return () => {
            isCurrentRequest = false;
        };
    }, [orderType, user_details?.factory_id]);

    const normalizeText = (value) => {
        if (value === null || value === undefined) return '';

        if (typeof value === 'string' || typeof value === 'number') {
            return String(value).trim();
        }

        if (Array.isArray(value)) {
            return value.map(normalizeText).filter(Boolean).join(', ');
        }

        if (typeof value === 'object') {
            return normalizeText(
                value.name ||
                value.full_name ||
                value.company_name ||
                value.business_name ||
                value.client_name ||
                value.project_name ||
                value.value ||
                value.label ||
                value.description ||
                ''
            );
        }

        return '';
    };

    const pickFirst = (...values) => {
        for (const value of values) {
            const normalized = normalizeText(value);
            if (normalized) return normalized;
        }

        return '';
    };

    const normalizeFieldName = (name) => {
        return normalizeText(name)
            .replace(/\s+/g, ' ')
            .replace(':', '')
            .trim()
            .toLowerCase();
    };

    const getFieldName = (field) => {
        if (!field || typeof field !== 'object') return '';

        return (
            field.name ||
            field.field_name ||
            field.input_name ||
            field.label ||
            field.title ||
            field.key ||
            field.input?.name ||
            field.field?.name ||
            field.order_input?.name ||
            ''
        );
    };

    const getFieldValue = (field) => {
        if (!field || typeof field !== 'object') return '';

        return (
            field.value ||
            field.text ||
            field.field_value ||
            field.input_value ||
            field.answer ||
            ''
        );
    };

    const getCustomFieldByName = (possibleNames = []) => {
        const fields =
            order?.custom_inputs ||
            order?.customFields ||
            order?.custom_fields ||
            order?.custom_fields_values ||
            order?.dynamic_fields ||
            order?.inputs ||
            [];

        if (!fields) return '';

        const normalizedPossibleNames = possibleNames.map(normalizeFieldName);
        const matchingDefinition = customInputDefinitions.find((field) =>
            normalizedPossibleNames.includes(normalizeFieldName(getFieldName(field)))
        );

        if (Array.isArray(fields)) {
            for (const field of fields) {
                const fieldName = normalizeFieldName(getFieldName(field));

                if (fieldName && normalizedPossibleNames.includes(fieldName)) {
                    return normalizeText(getFieldValue(field));
                }
            }
        }

        if (!Array.isArray(fields) && typeof fields === 'object') {
            const definitionId = matchingDefinition?._id || matchingDefinition?.id;
            if (definitionId && fields[definitionId] !== undefined && fields[definitionId] !== null) {
                return normalizeText(fields[definitionId]);
            }

            for (const possibleName of possibleNames) {
                const value = fields[possibleName];

                if (value !== undefined && value !== null) {
                    return normalizeText(value);
                }
            }
        }

        return '';
    };

    const formatDate = (date) => {
        if (!date) return '';

        const d = new Date(date);

        if (Number.isNaN(d.getTime())) return '';

        return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1)
            .toString()
            .padStart(2, '0')}/${d.getFullYear()}`;
    };

    const clientName = pickFirst(
        order?.client_name,
        order?.customer_name,
        order?.client?.name,
        order?.customer?.name
    );

    const customerPays = pickFirst(
        order?.customer_pays,
        order?.paying_customer,
        order?.payer_name,
        order?.payer,
        getCustomFieldByName([
            'לקוח משלם',
            'Customer pays',
            'Paying customer',
            'Payer'
        ]),
        clientName
    );

    const contactName = pickFirst(
        order?.contact_name,
        order?.contact_person,
        order?.contactPerson,
        order?.client_contact_name,
        order?.contact?.name,
        getCustomFieldByName([
            'איש קשר',
            'פרטי איש קשר',
            'Contact person',
            'Contact name',
            'Contact'
        ])
    );

    const projectName = pickFirst(
        order?.project_name,
        order?.project?.name,
        order?.client_project_name,
        order?.site_name,
        order?.project,
        getCustomFieldByName([
            'שם פרויקט',
            'שם לקוח / פרויקט',
            'פרויקט',
            'Project',
            'Project name'
        ]),
        clientName
    );

    const cityName = normalizeText(order?.city);

    const siteAddress = pickFirst(
        order?.site_address,
        order?.address,
        order?.full_address,
        getCustomFieldByName([
            'כתובת אתר',
            'כתובת',
            'Address',
            'Site address'
        ]),
        cityName
    );

    const phone = pickFirst(
        order?.client_phone,
        order?.phone,
        order?.contact_phone,
        order?.contactPhone,
        order?.second_phone,
        order?.contact?.phone,
        getCustomFieldByName([
            'מספר טלפון',
            'טלפון',
            'נייד',
            'Phone',
            'Mobile',
            'Contact phone'
        ])
    );

    const email = pickFirst(
        order?.client_email,
        order?.email,
        order?.contact_email,
        order?.contactEmail,
        order?.contact?.email,
        getCustomFieldByName([
            'מייל',
            'אימייל',
            'דואר אלקטרוני',
            'Email',
            'Contact email'
        ])
    );

    const dueDate = pickFirst(
        order?.due_date,
        order?.plannedDueDate,
        order?.planned_due_date
    );

    return (
        <div id="project-print-document" className="project-print-document" dir="rtl">
            {/* PAGE 1 */}
            <div className="print-page">
                <div className="print-logo">
                    <img
                        style={
                            decodeURIComponent(window.location.pathname).includes('מטלפרס')
                                ? { maxWidth: 'none' }
                                : {}
                        }
                        className="logo"
                        src={getLogo(user_details)}
                        alt="logo"
                    />
                </div>

                <div className="print-date">
                    {formatDate(dueDate)}
                </div>

                <div className="project-info">
                    <div className="info-row">
                        <div className="info-cell info-cell--right">
                            <strong>לכבוד:</strong>
                            <span>{customerPays}</span>
                        </div>
                    </div>

                    <div className="info-row">
                        <div className="info-cell info-cell--right">
                            <strong>לידי:</strong>
                            <span>{contactName}</span>
                        </div>
                    </div>

                    <div className="info-row">
                        <div className="info-cell info-cell--right">
                            <strong>פרויקט:</strong>
                            <span>{projectName}</span>
                        </div>

                        <div className="info-cell info-cell--left">
                            <strong>כתובת אתר:</strong>
                            <span>{siteAddress}</span>
                        </div>
                    </div>

                    <div className="info-row">
                        <div className="info-cell info-cell--right">
                            <strong>פרטי איש קשר:</strong>
                            <span>{contactName}</span>
                        </div>

                        <div className="info-cell info-cell--left">
                            <strong>מספר טלפון:</strong>
                            <span>{phone}</span>
                        </div>
                    </div>

                    <div className="info-row">
                        <div className="info-cell info-cell--right"></div>

                        <div className="info-cell info-cell--left">
                            <strong>מייל:</strong>
                            <span>{email}</span>
                        </div>
                    </div>
                </div>

                <h3 className="print-subject">
                    הנדון: תיאום ציפיות + לו"ז לפרויקט
                </h3>

                <p className="print-text">
                    לקוח יקר, בהמשך לשיחת תיאום ציפיות שנערכה איתך, להלן לו"ז
                    הפרויקט שאליו נוכל להתחייב.
                </p>

                <p className="print-text">
                    על מנת להכניס את הפרויקט לתכנון, אתה נדרש לספק את המסמכים הבאים:
                </p>

                <div className="requirements">
                    <p>1. תכניות אדריכלות</p>
                    <p>2. תכניות גמרים / עיצוב פנים, חיפויים, חזיתות, חתכים וכו'</p>
                    <p>3. תכניות בטיחות אש מעודכנות</p>
                    <p>4. רשימת מסגרות / אלומיניום</p>
                    <p>5. קבוצות פרזול, במידה ויש דלתות</p>

                    <p>• את התכניות יש להעביר בפורמט PDF / DWG</p>

                    <p>6. שם אדריכל - ______________</p>
                    <p>7. יועץ בטיחות __________ מספר טלפון ___________</p>
                    <p>8. מנהל בשטח – ____________________</p>

                    <p>• נגישות / לוגיסטיקה: דרך מדרגות / מעלית משא</p>
                    <p>• מוכנות האתר –</p>
                    <p>• נעילות / פרזול –</p>
                </div>
            </div>

            {/* PAGE 2 */}
            <div className="print-page print-page--second">
                <p className="schedule-title">
                    *להלן הלו"ז ע"פ השלבים:
                </p>

                <table className="schedule-table">
                    <thead>
                        <tr>
                            <th>משימה</th>
                            <th>שבוע</th>
                            <th>תאריך</th>
                            <th>הערות</th>
                        </tr>
                    </thead>

                    <tbody>
                        {sortedOrderProcesses.length > 0 ? (
                            sortedOrderProcesses.map((item, index) => (
                                <tr key={index}>
                                    <td>{item.process_name || ''}</td>
                                    <td>{item.week_number || ''}</td>
                                    <td>{item.week_date || ''}</td>
                                    <td>{item.admin_remark || ''}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="4"></td>
                            </tr>
                        )}
                    </tbody>
                </table>

                <div className="print-notes">
                    <p>
                        • לו"ז זה הינו התחייבות דו צדדית לעמידה משותפת ביעדים.
                    </p>

                    <p>
                        • חשוב להדגיש כי אי עמידה בשלבים הבאים תגרור שינוי של
                        זמני אספקה ומסירה: אישור תוכניות / מדידה לייצור
                        בהנחה שהאתר אינו מוכן / הגשת מסמכי לקוח + אנשי קשר.
                    </p>

                    <p>
                        • זמנים חדשים יינתנו ע"פ זמינות החברה.
                    </p>

                    <br />

                    <p>בברכה,</p>
                    <p>מחלקת תפ"י,</p>
                    <p>מטלפרס דלתות</p>
                </div>
            </div>
        </div>
    );
};

export default PrintOrderDetails;